/**
 * 工作台 —— HR 进系统第一屏。
 * 4 大核心模块 portal cards + KPI + 最近活动。
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { listCandidates, listInterviews, listJobs } from '../api/interview';
import { useAuth } from '../lib/AuthContext';
import type { Candidate, Interview, Job } from '../api/types';

interface Evaluation { overall_score: number | null; recommendation: string | null; }

export default function Home() {
  const nav = useNavigate();
  const { tenant } = useAuth();
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [evaluations, setEvaluations] = useState<Map<string, Evaluation>>(new Map());
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [iv, jb, cd] = await Promise.all([listInterviews(), listJobs(), listCandidates()]);
      setInterviews(iv); setJobs(jb); setCandidates(cd);

      const completed = iv.filter((i) => i.status === 'completed').slice(0, 10);
      const evMap = new Map<string, Evaluation>();
      await Promise.all(
        completed.map(async (i) => {
          try {
            const r = await fetch(`/api/interviews/${i.id}/full`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` },
            });
            if (r.ok) {
              const data = await r.json();
              if (data.evaluation) evMap.set(i.id, data.evaluation);
            }
          } catch {}
        })
      );
      setEvaluations(evMap);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void refresh(); }, []);

  const jobMap = useMemo(() => new Map(jobs.map((j) => [j.id, j])), [jobs]);
  const candMap = useMemo(() => new Map(candidates.map((c) => [c.id, c])), [candidates]);

  // 数据
  const ongoing = interviews.filter((i) => ['ongoing', 'launching'].includes(i.status));
  const scheduled = interviews.filter((i) => i.status === 'scheduled');
  const completed = interviews.filter((i) => i.status === 'completed');
  const pendingReports = completed.filter((i) => evaluations.has(i.id));
  const recentReports = pendingReports.slice(0, 5);

  return (
    <div className="container">
      {/* 欢迎区 */}
      <div className="page-header">
        <div>
          <h1 className="title">工作台</h1>
          <div className="subtitle">
            欢迎回来,{tenant?.company_name || ''}
          </div>
        </div>
      </div>

      {/* 4 大核心模块 */}
      <div className="module-grid">
        <ModuleCard
          tone="indigo"
          icon="📋"
          title="招聘需求"
          stat={String(jobs.length)}
          desc={jobs.length === 0 ? '还没有岗位' : `共 ${jobs.length} 个岗位`}
          action="新建岗位"
          onClick={() => nav('/admin/jobs/new')}
          secondaryAction={jobs.length > 0 ? '管理岗位' : undefined}
          onSecondary={() => nav('/admin/jobs')}
        />
        <ModuleCard
          tone="emerald"
          icon="📄"
          title="简历筛选"
          stat={String(candidates.length)}
          desc={candidates.length === 0 ? '还没有候选人' : `共 ${candidates.length} 位候选人`}
          action="上传简历"
          onClick={() => {
            // 上传简历需先选岗位 —— 跳到岗位列表
            if (jobs.length === 0) nav('/admin/jobs/new');
            else nav('/admin/jobs');
          }}
        />
        <ModuleCard
          tone="amber"
          icon="🎤"
          title="发起面试"
          stat={ongoing.length > 0 ? `${ongoing.length} 进行中` : String(scheduled.length)}
          desc={
            ongoing.length > 0 ? `${ongoing.length} 场进行中,${scheduled.length} 场待开始`
            : scheduled.length > 0 ? `${scheduled.length} 场待开始` : '还没有面试'
          }
          action="新建面试"
          onClick={() => nav('/admin/new')}
          secondaryAction={interviews.length > 0 ? '查看全部' : undefined}
          onSecondary={() => nav('/admin/interviews')}
        />
        <ModuleCard
          tone="blue"
          icon="📊"
          title="评估报告"
          stat={String(pendingReports.length)}
          desc={pendingReports.length === 0 ? '面试结束后自动生成报告' : `${pendingReports.length} 份报告`}
          action="查看报告"
          onClick={() => nav('/admin/interviews?filter=completed')}
        />
      </div>

      {/* 最近活动 */}
      {!loading && (
        <div className="row" style={{ alignItems: 'flex-start', gap: 16 }}>
          {/* 最近面试 */}
          <div className="card" style={{ flex: 1, marginBottom: 0 }}>
            <div className="section-title">
              <div className="name">📅 最近面试 <span className="count">{interviews.slice(0, 5).length}</span></div>
              <a onClick={() => nav('/admin/interviews')} style={{ cursor: 'pointer', fontSize: 13 }}>查看全部 →</a>
            </div>
            {interviews.length === 0 ? (
              <div className="empty">
                <div className="icon">🌤️</div>
                <div>还没有面试</div>
              </div>
            ) : (
              <div className="col" style={{ gap: 0 }}>
                {interviews.slice(0, 5).map((iv, idx) => {
                  const cand = candMap.get(iv.candidate_id);
                  const job = jobMap.get(iv.job_id);
                  return (
                    <div key={iv.id}
                         className="row card-clickable"
                         style={{
                           padding: '12px 0',
                           borderBottom: idx < 4 ? '1px solid var(--border)' : 'none',
                           gap: 12,
                         }}
                         onClick={() => nav(`/admin/interview/${iv.id}`)}>
                      <Avatar value={job?.interviewer_avatar} name={job?.interviewer_name} size={32} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>
                          {cand?.name || '(未命名)'}
                        </div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                          {job?.title || '?'} · {new Date(iv.scheduled_at).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <span className={`tag ${iv.status}`}>{statusLabel(iv.status)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 最近报告 */}
          <div className="card" style={{ flex: 1, marginBottom: 0 }}>
            <div className="section-title">
              <div className="name">📊 最近报告 <span className="count">{recentReports.length}</span></div>
            </div>
            {recentReports.length === 0 ? (
              <div className="empty">
                <div className="icon">📝</div>
                <div>还没有完成的报告</div>
              </div>
            ) : (
              <div className="col" style={{ gap: 0 }}>
                {recentReports.map((iv, idx) => {
                  const cand = candMap.get(iv.candidate_id);
                  const job = jobMap.get(iv.job_id);
                  const ev = evaluations.get(iv.id)!;
                  return (
                    <div key={iv.id}
                         className="row card-clickable"
                         style={{
                           padding: '12px 0',
                           borderBottom: idx < recentReports.length - 1 ? '1px solid var(--border)' : 'none',
                           gap: 12,
                         }}
                         onClick={() => nav(`/admin/interview/${iv.id}`)}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>{cand?.name || '(未命名)'}</div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{job?.title || '?'}</div>
                      </div>
                      <span className={`score ${scoreClass(ev.overall_score)}`}>
                        <span className="num">{ev.overall_score?.toFixed(1) ?? '—'}</span>
                      </span>
                      <span className={`rec ${ev.recommendation || 'pending'}`}>{recLabel(ev.recommendation)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ModuleCard({
  tone, icon, title, stat, desc, action, onClick, secondaryAction, onSecondary,
}: {
  tone: 'indigo' | 'emerald' | 'amber' | 'blue';
  icon: string;
  title: string;
  stat: string;
  desc: string;
  action: string;
  onClick: () => void;
  secondaryAction?: string;
  onSecondary?: () => void;
}) {
  return (
    <div className={`module-card ${tone}`} onClick={onClick}>
      <div className="module-icon">{icon}</div>
      <div className="module-title">{title}</div>
      <div className="module-stat">{stat}</div>
      <div className="module-desc">{desc}</div>
      <div className="row" style={{ gap: 8 }}>
        <span className="module-action">{action} →</span>
        {secondaryAction && (
          <a onClick={(e) => { e.stopPropagation(); onSecondary?.(); }}
            style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
            {secondaryAction}
          </a>
        )}
      </div>
    </div>
  );
}

function scoreClass(s: number | null | undefined): string {
  if (s == null) return 'none';
  if (s >= 8) return 'high';
  if (s >= 6) return 'mid';
  return 'low';
}
function statusLabel(s: string): string {
  return ({
    scheduled: '待开始', launching: '启动中', ongoing: '面试中',
    completed: '已完成', failed: '失败', cancelled: '已取消',
  } as Record<string, string>)[s] || s;
}
function recLabel(r: string | null | undefined): string {
  if (!r) return '待评估';
  return ({
    strong_yes: '强烈推荐', yes: '推荐', maybe: '可考虑', no: '不推荐',
    推荐进入下一轮: '推荐', 考虑: '可考虑',
  } as Record<string, string>)[r] || r;
}
